import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib/api.js'
import { ago, dateTime, money } from '../../../lib/format.js'
import { Card, Empty, KeyValue, Notice, PageHead, RowsSkeleton, Stat, StatsSkeleton, Tabs } from '../../../components/ui.js'
import { ConfirmDialog, toast } from '../../../components/feedback.js'
import { BellIcon, CheckCircleIcon, ClockIcon, UsersIcon, XCircleIcon } from '../../../components/icons.js'
import { NotificationTable } from './History.js'
import { TemplatePreviewPane, useTemplates } from './Wizard.js'
import {
  FILTER_FIELDS,
  OPS,
  PriorityBadge,
  ROUTING_LABEL,
  StatusBadge,
  errorMessage,
  formFromRule,
  ruleSummary,
  scheduleText,
  triggerText,
  type Rule,
} from './shared.js'

interface RuleDetail {
  rule: Rule
  currentlyTriggered: number
  runs: Array<{
    id: string
    slot: string
    status: string
    startedAt: string
    completedAt: string | null
    matching: number
    queued: number
    skipped: number
    error: string | null
  }>
}

type Tab = 'overview' | 'audience' | 'trigger' | 'channels' | 'templates' | 'schedule' | 'history' | 'runs'

export function AdminWalletAlertRulePage(): React.ReactNode {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('overview')
  const [confirmDelete, setConfirmDelete] = useState(false)

  const detail = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'rule', id],
    queryFn: () => api<RuleDetail>(`/v1/admin/wallet-alerts/rules/${id}`),
    refetchInterval: 60_000,
  })

  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: ['admin', 'wallet-alerts'] })

  const status = useMutation({
    mutationFn: (next: 'active' | 'paused') => api(`/v1/admin/wallet-alerts/rules/${id}/status`, { method: 'POST', body: { status: next } }),
    onSuccess: (_, next) => {
      toast('ok', next === 'active' ? 'Rule resumed' : 'Rule paused', next === 'paused' ? 'Queued notifications were cancelled.' : undefined)
      refresh()
    },
    onError: (error) => toast('error', 'Could not change status', errorMessage(error)),
  })

  const duplicate = useMutation({
    mutationFn: () => api<Rule>(`/v1/admin/wallet-alerts/rules/${id}/duplicate`, { method: 'POST' }),
    onSuccess: (rule) => {
      toast('ok', `Duplicated as “${rule.name}”`)
      navigate(`/admin/wallet-alerts/${rule.id}`)
    },
  })

  const remove = useMutation({
    mutationFn: () => api(`/v1/admin/wallet-alerts/rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('ok', 'Rule deleted')
      refresh()
      navigate('/admin/wallet-alerts')
    },
    onError: (error) => toast('error', 'Could not delete', errorMessage(error)),
  })

  const crumbs = [{ label: 'Wallet Alerts', to: '/admin/wallet-alerts' }, { label: detail.data?.rule.name ?? 'Rule' }]

  if (detail.error) {
    return (
      <>
        <PageHead title="Alert rule" crumbs={crumbs} />
        <Card>
          <Empty error title="Could not load this rule">
            {errorMessage(detail.error)}
          </Empty>
        </Card>
      </>
    )
  }
  if (!detail.data) {
    return (
      <>
        <PageHead title="Alert rule" crumbs={crumbs} />
        <StatsSkeleton count={4} />
      </>
    )
  }

  const { rule, runs, currentlyTriggered } = detail.data
  const form = formFromRule(rule)

  return (
    <>
      <PageHead
        title={rule.name}
        subtitle={rule.description ?? undefined}
        crumbs={crumbs}
        actions={
          <div className="wa-head-actions">
            <StatusBadge status={rule.status} />
            <PriorityBadge priority={rule.priority} />
            <Link className="btn" to={`/admin/wallet-alerts/${id}/edit`}>
              Edit
            </Link>
            <Link className="btn" to={`/admin/wallet-alerts/${id}/edit?step=6`}>
              Test
            </Link>
            <button type="button" className="btn" disabled={duplicate.isPending} onClick={() => duplicate.mutate()}>
              Duplicate
            </button>
            {rule.status === 'active' ? (
              <button type="button" className="btn" disabled={status.isPending} onClick={() => status.mutate('paused')}>
                Pause
              </button>
            ) : (
              <button type="button" className="btn btn--primary" disabled={status.isPending} onClick={() => status.mutate('active')}>
                {rule.status === 'paused' ? 'Resume' : 'Activate'}
              </button>
            )}
            <button type="button" className="btn btn--danger" onClick={() => setConfirmDelete(true)}>
              Delete
            </button>
          </div>
        }
      />

      <section className="wa-stats">
        <Stat label="Currently triggered" value={currentlyTriggered} icon={<UsersIcon size={20} />} tone="var(--warn)" footnote="merchants in the alert state" />
        <Stat label="Alerts sent" value={rule.counts.sent} icon={<CheckCircleIcon size={20} />} tone="var(--ok)" />
        <Stat label="Alerts failed" value={rule.counts.failed} icon={<XCircleIcon size={20} />} tone="var(--bad)" />
        <Stat label="Skipped / cancelled" value={rule.counts.skipped} icon={<BellIcon size={20} />} tone="var(--ink-faint)" />
        <Stat
          label="Next execution"
          value={<span style={{ fontSize: '1rem' }}>{rule.status === 'active' && rule.nextRunAt ? dateTime(rule.nextRunAt) : '—'}</span>}
          icon={<ClockIcon size={20} />}
          footnote={rule.lastRunAt ? `Last ran ${ago(rule.lastRunAt)}` : 'Never run'}
        />
      </section>

      <Card flush>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { key: 'overview', label: 'Overview' },
            { key: 'audience', label: 'Audience' },
            { key: 'trigger', label: 'Trigger conditions' },
            { key: 'channels', label: 'Channels' },
            { key: 'templates', label: 'Templates' },
            { key: 'schedule', label: 'Schedule' },
            { key: 'history', label: 'Notification history' },
            { key: 'runs', label: 'Execution logs', count: runs.length },
          ]}
        />

        <div style={{ padding: tab === 'history' || tab === 'runs' ? 0 : 16 }}>
          {tab === 'overview' && (
            <div className="wa-form">
              <div className="wa-summary">{ruleSummary(form)}</div>
              {rule.status === 'active' && !rule.nextRunAt && <Notice kind="warn" title="No upcoming run">The schedule has ended. Edit it or pause the rule.</Notice>}
              <KeyValue
                rows={[
                  { label: 'Category', value: rule.category.replace(/_/g, ' ') },
                  { label: 'Trigger', value: triggerText(rule) },
                  { label: 'Channels', value: ROUTING_LABEL[rule.channels.routing] },
                  { label: 'Schedule', value: scheduleText(rule.schedule) },
                  { label: 'Last triggered', value: rule.lastTriggeredAt ? dateTime(rule.lastTriggeredAt) : 'Never' },
                  { label: 'Created', value: dateTime(rule.createdAt) },
                  { label: 'Updated', value: dateTime(rule.updatedAt) },
                ]}
              />
              <h3 style={{ fontSize: '.9rem' }}>Recent trigger events</h3>
              <NotificationTable ruleId={id} compact />
            </div>
          )}

          {tab === 'audience' && (
            <KeyValue
              rows={[
                { label: 'Mode', value: rule.audience.mode === 'all' ? 'All merchants' : rule.audience.mode === 'specific' ? 'Specific merchants' : 'Dynamic filters' },
                { label: 'Inactive accounts', value: rule.audience.includeInactive ? 'Included' : 'Excluded' },
                ...(rule.audience.mode === 'specific' ? [{ label: 'Merchants', value: rule.audience.merchantIds.map((m) => `#${m}`).join(', ') }] : []),
                ...(rule.audience.mode === 'filters'
                  ? rule.audience.groups.map((group, i) => ({
                      label: `Group ${i + 1} (${group.combinator.toUpperCase()})${i > 0 ? ` · joined by ${rule.audience.combinator.toUpperCase()}` : ''}`,
                      value: group.conditions
                        .map((c) => {
                          const field = FILTER_FIELDS.find((f) => f.key === c.field)
                          const op = OPS[field?.kind ?? 'text']?.find((o) => o.key === c.op)
                          return `${field?.label ?? c.field} ${op?.label ?? c.op}${c.value !== null && c.value !== '' ? ` ${c.value}` : ''}`
                        })
                        .join(group.combinator === 'and' ? ' AND ' : ' OR '),
                    }))
                  : []),
              ]}
            />
          )}

          {tab === 'trigger' && (
            <KeyValue
              rows={[
                { label: 'Condition', value: triggerText(rule) },
                { label: 'Currency', value: rule.currency ?? 'Each wallet in its own currency' },
                { label: 'Recovery threshold', value: rule.triggerConfig.recoveryAmount !== null ? money(rule.triggerConfig.recoveryAmount, rule.currency ?? 'INR') : 'Re-arm when the condition clears' },
                { label: 'Repeat policy', value: rule.frequency.repeat === 'once_per_crossing' ? 'Once per crossing' : `Every ${rule.frequency.minIntervalHours}h while true` },
                { label: 'Cooldown', value: rule.frequency.cooldownHours > 0 ? `${rule.frequency.cooldownHours}h` : 'None' },
                { label: 'Max per merchant per day', value: rule.frequency.maxPerDay },
              ]}
            />
          )}

          {tab === 'channels' && (
            <KeyValue
              rows={[
                { label: 'Routing', value: ROUTING_LABEL[rule.channels.routing] },
                { label: 'WhatsApp template', value: rule.whatsappTemplateName ?? '—' },
                { label: 'Email template', value: rule.emailTemplateName ?? '—' },
                { label: 'Sender name', value: rule.channels.fromName || 'Platform default' },
                { label: 'Reply-to', value: rule.channels.replyTo || '—' },
              ]}
            />
          )}

          {tab === 'templates' && <RuleTemplates rule={rule} />}

          {tab === 'schedule' && (
            <KeyValue
              rows={[
                { label: 'Schedule', value: scheduleText(rule.schedule) },
                { label: 'Timezone', value: rule.schedule.timezone },
                { label: 'Starts', value: rule.schedule.startAt ? dateTime(rule.schedule.startAt) : 'Immediately' },
                { label: 'Ends', value: rule.schedule.endAt ? dateTime(rule.schedule.endAt) : 'Never' },
                { label: 'Quiet hours', value: rule.schedule.quietHours.enabled ? `${rule.schedule.quietHours.start} – ${rule.schedule.quietHours.end}` : 'Off' },
                { label: 'Business days only', value: rule.schedule.businessDaysOnly ? 'Yes' : 'No' },
                { label: 'Max per run', value: rule.schedule.maxPerRun },
                { label: 'Next run', value: rule.nextRunAt ? dateTime(rule.nextRunAt) : '—' },
                { label: 'Last run', value: rule.lastRunAt ? dateTime(rule.lastRunAt) : 'Never' },
              ]}
            />
          )}

          {tab === 'history' && <NotificationTable ruleId={id} />}

          {tab === 'runs' &&
            (runs.length === 0 ? (
              <Empty icon={<ClockIcon size={24} />} title="No runs yet">
                {rule.status === 'active' ? 'The first run happens at the next scheduled time.' : 'Activate the rule to start running it.'}
              </Empty>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Run</th>
                      <th>Started</th>
                      <th>Status</th>
                      <th className="right">Matching</th>
                      <th className="right">Alerted</th>
                      <th className="right">Skipped</th>
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((run) => (
                      <tr key={run.id}>
                        <td>
                          #{run.id}
                          <span className="table__sub">slot {dateTime(run.slot)}</span>
                        </td>
                        <td>
                          {dateTime(run.startedAt)}
                          {run.completedAt && <span className="table__sub">{Math.max(0, (new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime()) / 1000).toFixed(1)}s</span>}
                        </td>
                        <td>
                          <StatusBadge status={run.status === 'completed' ? 'sent' : run.status === 'failed' ? 'failed' : 'queued'} />
                        </td>
                        <td className="right num">{run.matching}</td>
                        <td className="right num">{run.queued}</td>
                        <td className="right num">{run.skipped}</td>
                        <td style={{ maxWidth: 320, whiteSpace: 'pre-wrap', fontSize: '.8rem', color: run.error ? 'var(--bad)' : undefined }}>{run.error ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </div>
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this rule?"
        message="Queued notifications are cancelled; the sent history is kept. This cannot be undone."
        confirmLabel="Delete rule"
        danger
        pending={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  )
}

function RuleTemplates({ rule }: { rule: Rule }): React.ReactNode {
  const templates = useTemplates()
  if (!templates.data) return <RowsSkeleton rows={4} />

  const chosen = templates.data.items.filter((t) => t.id === rule.whatsappTemplateId || t.id === rule.emailTemplateId)
  if (chosen.length === 0) return <Empty title="No templates selected" />

  return (
    <div className="grid grid--2">
      {chosen.map((template) => (
        <div key={template.id} className="wa-form">
          <strong>
            {template.channel === 'email' ? 'Email' : 'WhatsApp'} — {template.name} <span className="faint">v{template.version}</span>
          </strong>
          <TemplatePreviewPane template={template} threshold={rule.thresholdAmount} />
        </div>
      ))}
    </div>
  )
}
